/**
 * GP-pathway landing screen — the target of the SMS link
 * (`https://wire.cerinahealth.com/gp/<token>`).
 *
 * One job: pass the identity gate. The resident confirms their date of birth,
 * we exchange the single-use token for a started, prefilled conversation, cache
 * that session the same way a refresh does (PII-stripped transcript in
 * localStorage), and hand over to the normal chat at `/` — no separate
 * conversation surface, no changes to useConversation.
 *
 * Failure copy is exactly the server's uniform message. The backend refuses to
 * reveal whether a token is unknown, spent, expired or the DOB was wrong, and
 * this screen must not undo that by editorialising.
 *
 * English-only for now, like the rest of the pre-conversation chrome: the
 * language switcher lives in the conversation the resident lands in next.
 */
import { useState } from "react";
import { getConversation, verifyGpSession } from "../api/client";
import { ApiError } from "../api/types";
import { clearSession, saveSession } from "../conversation/persistence";
import "./GpEntry.css";

interface Props {
  token: string;
}

export function GpEntry({ token }: Props) {
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const digits = (v: string, max: number) => v.replace(/\D/g, "").slice(0, max);
  const complete = day.length >= 1 && month.length >= 1 && year.length === 4;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!complete || busy) return;
    setBusy(true);
    setError(null);
    try {
      const dob = `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
      const verified = await verifyGpSession(token, dob);
      // Fetch the full opening turn (the verify response is a summary without
      // options), cache it exactly as a refresh would, and let the normal
      // restore path at `/` take over. Clear first: on a shared device a
      // previous person's cached session must never survive into this one.
      const turn = await getConversation(verified.session_id);
      clearSession();
      saveSession(verified.session_id, [{ id: "g1", role: "bot", turn }]);
      // Hand over to the chat at the app's base ("/" in preview, "/llr/" in
      // production) — the restore path takes it from the cache written above.
      // Carry `?api=` through: it is how this page was told which backend to
      // talk to, and the chat must keep talking to the SAME one or it would
      // look for a session that lives in another environment's store.
      const api = new URLSearchParams(window.location.search).get("api");
      const base = import.meta.env.BASE_URL;
      window.location.replace(
        api ? `${base}?api=${encodeURIComponent(api)}` : base,
      );
    } catch (err) {
      setError(
        err instanceof ApiError && err.status !== null
          ? err.message
          : "We couldn't check your details. Please check your connection and try again.",
      );
      setBusy(false);
    }
  }

  return (
    <div className="gp-entry">
      <main className="gp-entry-card" aria-labelledby="gp-entry-heading">
        <h1 id="gp-entry-heading" className="gp-entry-heading">
          Welcome to WIRE
        </h1>
        <p className="gp-entry-lede">
          Your GP has referred you for work and health support. Before we
          begin, we need to check this link was opened by the right person.
        </p>

        <form onSubmit={submit} noValidate>
          <fieldset className="gp-dob-fieldset">
            <legend className="gp-dob-legend">
              Please confirm your date of birth
            </legend>
            <p className="gp-dob-hint" id="gp-dob-hint">
              For example, 27 3 1985
            </p>
            <div className="gp-dob-fields" aria-describedby="gp-dob-hint">
              <label className="gp-dob-field">
                <span>Day</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="bday-day"
                  className="gp-dob-input gp-dob-2"
                  value={day}
                  onChange={(e) => setDay(digits(e.target.value, 2))}
                />
              </label>
              <label className="gp-dob-field">
                <span>Month</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="bday-month"
                  className="gp-dob-input gp-dob-2"
                  value={month}
                  onChange={(e) => setMonth(digits(e.target.value, 2))}
                />
              </label>
              <label className="gp-dob-field">
                <span>Year</span>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="bday-year"
                  className="gp-dob-input gp-dob-4"
                  value={year}
                  onChange={(e) => setYear(digits(e.target.value, 4))}
                />
              </label>
            </div>
          </fieldset>

          {error && (
            <p className="gp-entry-error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            className="gp-entry-continue"
            disabled={!complete || busy}
          >
            {busy ? "Checking…" : "Continue"}
          </button>
        </form>

        <p className="gp-entry-privacy">
          This link is for one person only. If this message wasn't meant for
          you, please close this page.
        </p>
      </main>
    </div>
  );
}
